import { ref } from 'vue'
import { defineStore } from 'pinia'
import { DateTime } from 'ts-luxon'
import { distance, midpoint, point } from '@turf/turf'

export const useMapAreaSelect = defineStore('map_area_select', () => {
  const isDrawing = ref(false)
  const startScreenCoords = ref([0, 0])
  const emits = ref<Function | null>(null)
  let startMapCoords: [number, number] = [0, 0]
  let timer = 0

  /**
   * Начало выделения области на карте.
   * Запоминает гео-координаты и экранные координаты начальной точки,
   * а блокировкой перетаскивания карты управляет parent controller.
   * Вызывается из обработчика mouseDown без Shift, когда выбран тип выделения области.
   */
  const startAreaSelect = (_: any, event: any) => {
    timer = DateTime.now().toMillis()
    startMapCoords = [...event.coordinates] as [number, number]
    startScreenCoords.value = [...event.screenCoordinates]
    isDrawing.value = true
  }

  /**
   * Завершение выделения области на карте.
   * Вычисляет результирующую область
   * и генерирует событие selected с данными выделения.
   * Игнорирует слишком быстрые клики (менее 300 мс) во избежание случайных срабатываний.
   * Для прямоугольника возвращает bbox [minLng, minLat, maxLng, maxLat],
   * для окружности — центр диаметра и радиус в метрах.
   */
  const endAreaSelect = (_: any, event: any, type: 'square' | 'circle') => {
    if (!isDrawing.value) return

    const timeout = DateTime.now().toMillis() - timer > 300
    isDrawing.value = false

    if (!timeout) return

    const endMapCoords = [...event.coordinates] as [number, number]

    let result: any
    if (type === 'square') {
      result = {
        type: 'bbox',
        bbox: [
          Math.min(startMapCoords[0], endMapCoords[0]),
          Math.min(startMapCoords[1], endMapCoords[1]),
          Math.max(startMapCoords[0], endMapCoords[0]),
          Math.max(startMapCoords[1], endMapCoords[1]),
        ],
      }
    } else {
      const startPoint = point(startMapCoords)
      const endPoint = point(endMapCoords)

      result = {
        type: 'circle',
        center: midpoint(startPoint, endPoint).geometry.coordinates,
        radius: Math.round(distance(startPoint, endPoint, { units: 'meters' }) / 2),
      }
    }

    if (emits.value) emits.value('selected', result)
  }

  /**
   * Отмена текущего выделения без генерации события.
   * Вызывается при нажатии Escape или при сбросе типа выделения.
   * Восстановлением поведения карты управляет parent controller.
   */
  const cancelAreaSelect = () => {
    if (!isDrawing.value) return
    isDrawing.value = false
  }

  return {
    isDrawing,
    startScreenCoords,
    emits,
    startAreaSelect,
    endAreaSelect,
    cancelAreaSelect,
  }
})
